<template>
  <div class="results">
    <h2 class="section-title large">
      {{ $t('results.title') }}
    </h2>

    <p
      v-if="!isLite"
      class="description"
    >
      {{ $t('results.description') }}
    </p>
    <!-- Filter Tabs -->
    <div class="filter-tabs">
      <button 
        v-for="tab in tabs" 
        :key="tab.id"
        class="tab-button"
        :class="{ active: activeTab === tab.id }"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Filter Content -->
    <div class="filter-content">
      <!-- By Date Filter -->
      <div
        v-if="activeTab === 'by-date'"
        class="date-filter"
      >
        <NextDrawsFilter
          v-model="selectedDate"
          :allow-past="true"
          :placeholder="$t('results.selectDate')"
          :fallback-to-today="false"
          no-interaction-highlight
          @update:modelValue="handleDateFilter"
        />
      </div>

      <!-- By Game Filter -->
      <div
        v-if="activeTab === 'by-lottery'"
        class="game-filter"
      >
        <CustomSelect
          v-model="selectedGame"
          :options="gamesList"
          :placeholder="$t('results.allGames')"
        />
      </div>

      <!-- By Draw ID Filter -->
      <div
        v-if="activeTab === 'by-drawid'"
        class="draw-filter"
      >
        <input
          :value="drawId"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          autocomplete="off"
          data-keyboard="numeric"
          :placeholder="$t('results.enterDrawId')"
          class="draw-input"
          @input="handleDrawIdInput"
        >
      </div>
    </div>

    <!-- Results Grid -->
    <div
      v-if="resultsStore.loading"
      class="results-skeleton-grid"
      aria-busy="true"
      aria-live="polite"
    >
      <div
        v-for="n in 4"
        :key="'skeleton-' + n"
        class="results-skeleton-card"
      >
        <div class="results-skeleton-line results-skeleton-line--title" />
        <div class="results-skeleton-line results-skeleton-line--meta" />
        <div class="results-skeleton-balls">
          <span
            v-for="ball in 6"
            :key="'ball-' + n + '-' + ball"
            class="results-skeleton-ball"
          />
        </div>
      </div>
    </div>
    <div
      v-else-if="resultsStore.error"
      class="results-status results-status--error"
    >
      {{ resultsStore.error }}
    </div>
    <div
      v-else-if="!resultsStore.results.length"
      class="empty-state"
    >
      <p>{{ $t('results.noDataAvailable') }}</p>
    </div>
    <div
      v-else
      class="results-grid"
    >
      <LotteryResultCard
        v-for="result in resultsStore.results"
        :key="result.id"
        :lottery="result"
        @bet="handleBet(result)"
      />
    </div>

    <!-- Load More Button -->
    <div
      v-if="resultsStore.hasMorePages"
      class="load-more"
    >
      <button
        class="load-more-button"
        :disabled="resultsStore.loadingMore"
        @click="loadMore"
      >
        {{ resultsStore.loadingMore ? $t('common.loading') : $t('results.loadMore') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { defineOptions } from 'vue'
import { useI18n } from 'vue-i18n'
import LotteryResultCard from '@/components/LotteryResultCard.vue'
import NextDrawsFilter from '@/components/NextDrawsFilter.vue'
import { useResultsStore } from '@/stores/results'
import { useGameStore } from '@/stores/game'
import { useRouter, useRoute } from 'vue-router'
import CustomSelect from '@/components/base/CustomSelect.vue'
import { useIntegrationMode } from '@/composables/useIntegrationMode'
// import { useAuthStore } from '@/stores/auth'
defineOptions({
  name: 'ResultsView'
})

const gameStore = useGameStore()
const resultsStore = useResultsStore();
// const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()
const { isLite } = useIntegrationMode()
const { t } = useI18n()

const tabs = computed(() => [
  { id: 'by-date', label: t('results.byDate') },
  { id: 'by-lottery', label: t('results.byGame') },
  { id: 'by-drawid', label: t('results.byDrawId') }
]);

const activeTab = ref('by-lottery'); // Default to BY GAME
const selectedDate = ref('');
const selectedGame = ref('');
const drawId = ref('');

const handleDrawIdInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const digitsOnly = input.value.replace(/\D/g, '');
  if (input.value !== digitsOnly) {
    input.value = digitsOnly;
  }
  drawId.value = digitsOnly;
};

const buildFilters = () => {
  const filters: Record<string, string> = {};

  if (activeTab.value === 'by-date') {
    filters.drawType = 'by-date';
    if (selectedDate.value) {
      filters.drawDate = selectedDate.value;
    }
  } else if (activeTab.value === 'by-lottery') {
    filters.drawType = 'by-lottery';
    if (selectedGame.value) {
      filters.gameSlug = selectedGame.value;
    }
  } else if (activeTab.value === 'by-drawid') {
    filters.drawType = 'draw';
    if (drawId.value.trim()) {
      filters.drawId = drawId.value.trim();
    }
  }

  return filters;
};

const refreshResults = () => {
  resultsStore.setFilters(buildFilters());
  resultsStore.fetchResults(1);
};

const loadMore = async () => {
  await resultsStore.loadMoreResults();
};

const handleDateFilter = (date: string) => {
  selectedDate.value = date;
};

const handleBet = async (game: any) => {
  await gameStore.fetchGameDetails(game.game_slug)
  router.push({ path: `/lottery/${game?.game_slug}`, query: route.query })
}

const gamesList = computed(() => {
  // Use game store to show ALL games, not just games with results
  const allGames = gameStore.getProducts || [];
  if (!Array.isArray(allGames)) return [];
  
  // Sort games alphabetically by name and map to { label, value }
  return [...allGames]
    .sort((a, b) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    })
    .map(g => ({
      label: g.name,
      value: g.slug
    }));
});

watch(activeTab, async (newTab, oldTab) => {
  if (oldTab === 'by-lottery' && newTab !== 'by-lottery') {
    selectedGame.value = '';
  }
  if (isLite.value && newTab === 'by-lottery' && (!gameStore.getProducts || gameStore.getProducts.length === 0)) {
    await gameStore.fetchProducts();
  }
});

watch(
  [activeTab, selectedDate, selectedGame, drawId],
  () => {
    refreshResults();
  }
);

onMounted(async () => {
  if (!isLite.value && (!gameStore.getProducts || gameStore.getProducts.length === 0)) {
    await gameStore.fetchProducts();
  }
  refreshResults();
});
</script>

<style scoped>
.results {
  background: var(--background);
  width: 100%;
  max-width: var(--results-page-max-width, none);
  margin: 0;
  padding: var(--results-page-padding, 5px);
  box-sizing: border-box;
}

.section-title.large {
  color: var(--results-title-color, var(--colour-base-text-colour));
  font-size: var(--results-title-size, 28px);
  font-weight: var(--results-title-weight, 700);
  margin: 0 0 10px;
}

.description {
  color: var(--results-description-color, var(--colour-secondary-text-colour));
  font-size: var(--results-description-size, 14px);
  line-height: 1.5;
  margin: 0 0 18px;
}

.draw-filter {
  text-align: center;
  color: var(--colour-base-text-colour);
}

/* Custom styles specific to ResultsView */

.filter-tabs {
  display: flex;
  gap: 8px;
  justify-content: center;
  width: 100%;
  max-width: var(--results-tabs-max-width, none);
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 12px;
  background: var(--results-tabs-bg, transparent) !important;
  border: var(--results-tabs-border-width, 0) solid var(--results-tabs-border, transparent);
  border-radius: var(--results-tabs-radius, 0);
  padding: var(--results-tabs-padding, 0);
  box-sizing: border-box;
}

.tab-button {
  flex: 1;
  min-height: var(--results-tab-height, 44px);
  border: var(--results-tab-border-width, 0) solid var(--results-tab-border-color, transparent);
  border-radius: var(--results-tab-radius, 6px);
  background: var(--colour-surface-cont);
  color: var(--colour-base-text-colour);
  font-size: var(--results-tab-size, 14px);
  font-weight: var(--results-tab-weight, 600);
  padding: var(--results-tab-padding, 10px 18px);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.tab-button:hover {
  background: var(--colour-surface-cont);
  color: var(--colour-base-text-colour);
}

.tab-button.active {
  background: var(--colour-orange-button);
  color: var(--text-button);
  box-shadow: var(--results-tab-active-shadow, none);
}

.filter-content {
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
  width: 100%;
}

.date-filter,
.game-filter,
.draw-filter {
  width: 100%;
  max-width: var(--results-filter-panel-max-width, var(--results-filter-max-width, none));
}

.game-filter :deep(.custom-select) {
  background: var(--colour-surface-cont-lowest);
  color: var(--colour-base-text-colour);
  border-color: var(--colour-surface-cont);
  text-align: center;
  text-align-last: center;
  padding-left: 18px;
  padding-right: 48px;
}

.date-button,
.game-select,
.draw-input {
  background: var(--colour-surface-cont-lowest);
  border: 1px solid var(--colour-surface-cont);
  color: var(--colour-base-text-colour);
  padding: 12px 24px;
  font-size: 16px;
  width: 100%;
  text-align: center;
  box-sizing: border-box;
}

.date-button:focus,
.game-select:focus,
.draw-input:focus {
  border-color: var(--bg-filter-select-focus, var(--colour-surface-cont));
  outline: var(--bg-filter-select-focus, var(--colour-surface-cont));
}

.date-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.calendar-icon {
  font-size: 20px;
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--results-grid-gap, 12px);
  margin-bottom: 32px;
  width: 100%;
}

.results-grid :deep(.lottery-result-card) {
  width: 100%;
}

.results-grid :deep(.content-section) {
  flex: 1;
  min-width: 0;
}

.results-grid :deep(.results) {
  width: 100%;
}

.results-grid :deep(.numbers-wrap) {
  width: 100%;
}

.load-more {
  display: flex;
  justify-content: center;
  margin-bottom: 48px;
}

.load-more-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.results-status {
  text-align: center;
  padding: 48px 20px;
  color: var(--colour-secondary-text-colour);
  font-size: 16px;
}

.results-status--error {
  color: var(--colour-red-text-colour, #df2357);
}

.results-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--lucky-numbers-grid-gap, 12px);
  margin-bottom: 24px;
}

.results-skeleton-card {
  background: var(--colour-surface-cont-lowest, var(--bg-secondary));
  border: 1px solid var(--colour-surface-cont, var(--border-color));
  border-radius: var(--result-card-radius, 8px);
  padding: var(--result-card-inner-padding-block, 10px) var(--result-card-inner-padding-inline, 10px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 120px;
}

.results-skeleton-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    var(--colour-surface-cont, #ddd) 0%,
    var(--colour-surface-cont-high, #eee) 50%,
    var(--colour-surface-cont, #ddd) 100%
  );
  background-size: 200% 100%;
  animation: results-skeleton-shimmer 1.2s ease-in-out infinite;
}

.results-skeleton-line--title {
  width: 70%;
  height: 14px;
}

.results-skeleton-line--meta {
  width: 45%;
}

.results-skeleton-balls {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.results-skeleton-ball {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--colour-surface-cont, #ddd);
  animation: results-skeleton-shimmer 1.2s ease-in-out infinite;
}

@keyframes results-skeleton-shimmer {
  0% { background-position: 200% 0; opacity: 0.7; }
  50% { opacity: 1; }
  100% { background-position: -200% 0; opacity: 0.7; }
}

.empty-state {
  text-align: center;
  padding: 48px 20px;
  color: var(--colour-secondary-text-colour);
  background: var(--detail-market-select-bg, var(--bg-secondary));
  border: 1px solid var(--result-card-border-color, var(--border-color));
  border-radius: var(--result-card-radius, 8px);
}

.empty-state p {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--colour-secondary-text-colour);
}

.results-info {
  text-align: center;
  color: var(--colour-secondary-text-colour);
  max-width: 1200px;
  margin: 0 auto;
}

.results-info p {
  margin-bottom: 8px;
}

.see-more-button {
  background: var(--colour-red-text-colour);
  border: none;
  color: var(--colour-base-text-colour);
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  margin-top: 16px;
}

@media (max-width: 1024px) {
  .results-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .results {
    padding: var(--results-page-mobile-padding, 0 4px);
  }

  .results-grid,
  .results-skeleton-grid {
    margin-left: 0;
    margin-right: 0;
    gap: var(--lucky-numbers-grid-mobile-gap, 10px);
    margin-bottom: 24px;
    grid-template-columns: 1fr;
  }

  .empty-state {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 24px 12px;
  }

  .empty-state p {
    font-size: 14px;
    font-weight: 400;
  }

  .filter-tabs {
    width: 100%;
  }

  .date-filter,
  .game-filter,
  .draw-filter {
    width: 100%;
  }

  .date-button,
  .game-select,
  .draw-input {
    width: 100%;
    /* ≥16px prevents iOS input zoom */
    font-size: 16px;
  }
}

.game-logo {
  width: 20px;
  height: 20px;
  vertical-align: middle;
  margin-right: 6px;
}

.game-select {
  display: flex;
  align-items: center;
  min-width: 220px;
  padding: 12px 12px;
  font-size: 16px;
  background: var(--colour-surface);
  color: var(--colour-base-text-colour);
  border-radius: 4px;
  border: 1px solid var(--colour-surface-cont);
  text-align: center;
}
</style>
